import { textColorFields } from '../_shared/textColor.js';
import { animationFields, mobilePaddingFields, paddingFields, sectionChromeFields, } from '../_shared/universal.js';
/**
 * countdown — A COUNTDOWN THE SELLER DROPS ON A PAGE (feature 0041 V5).
 *
 * The Countdown APP (0041 V1–V4) already paints four surfaces — the buy-box banner, the site-wide bar, the
 * card chip and the corner popup — but every one of them is placed by a CAMPAIGN in the dashboard and
 * injected by the backend. None of them is a section, so the builder canvas never showed a countdown at
 * all, and a seller who wanted one "here, between the hero and the reviews, this big, in these colours"
 * had nowhere to ask (founder, 28/09/2026).
 *
 * This is that section. It owns its own timer — it reads NO campaign and no app config, so it works on a
 * store that never installed the Countdown app — and it is configured the way every other section is: in
 * the inspector, in front of the canvas, with the result painted as you turn each knob.
 *
 * ## Three timers, and what each one promises
 *
 *  - **Jusqu'à une date** (`date`) — one shared deadline. Stored ABSOLUTE (the control converts the
 *    seller's wall clock to UTC), so every buyer on earth counts down to the same second. An optional
 *    `startsAt` keeps the whole section off the page until the sale opens: writing the section a week
 *    early is the normal way to prepare a launch.
 *  - **Une durée par visiteur** (`evergreen`) — X minutes from the visitor's first sight, kept in their
 *    browser. An expired evergreen timer STAYS expired (the anti-Shopyan property of `countdownClock`):
 *    that is why `Redémarrer` exists as an expiry answer, and why the field says so.
 *  - **Tous les jours à une heure** (`daily`) — `HH:MM` in the VISITOR's own timezone, rolling to
 *    tomorrow once it passes.
 *
 * ## Why so many knobs
 *
 * The brief was explicit: everything a seller could reasonably want to change, they can change here —
 * the typography (size and weight, per device), the tiles (shape, size, radius, gap, border, shadow),
 * every colour (digits, tiles, labels, border, button), the unit words themselves, which units even
 * show, the layout at each breakpoint, the button and what it does at zero. The rule applied throughout
 * is that a colour left untouched is `Automatic`: the component derives it from the section's colour
 * scheme, so an untouched countdown is already coherent with the theme and a touched one is exactly what
 * the picker showed.
 */
export const countdownSchema = {
    type: 'countdown',
    name: 'Countdown',
    version: 1,
    templates: ['index', 'collection', 'page', 'product'],
    fields: [
        // ── Timer ────────────────────────────────────────────────────────────────────
        {
            id: 'timerType',
            type: 'select',
            label: 'Timer',
            group: 'Timer',
            default: 'date',
            options: [
                { value: 'date', label: 'Until a date' },
                { value: 'evergreen', label: 'A duration per visitor' },
                { value: 'daily', label: 'Every day at a time' },
            ],
        },
        {
            id: 'endsAt',
            type: 'datetime',
            label: 'End date',
            group: 'Timer',
            info: 'The time you pick is your own; every visitor sees the same end.',
            visibleWhen: { field: 'timerType', equals: 'date' },
        },
        {
            id: 'startsAt',
            type: 'datetime',
            label: 'Show from',
            group: 'Timer',
            info: 'Leave empty to show it right away. Before this moment the section is not on the page.',
            visibleWhen: { field: 'timerType', equals: 'date' },
        },
        {
            id: 'minutes',
            type: 'number',
            label: 'Duration',
            group: 'Timer',
            info: 'Counted from each visitor’s first view, kept in their browser.',
            default: 15,
            min: 1,
            max: 20160,
            step: 1,
            unit: 'min',
            visibleWhen: { field: 'timerType', equals: 'evergreen' },
        },
        {
            id: 'dailyEndTime',
            type: 'datetime',
            mode: 'time',
            label: 'End time',
            group: 'Timer',
            info: 'Read in each visitor’s own timezone, then rolls over to the next day.',
            default: '23:59',
            visibleWhen: { field: 'timerType', equals: 'daily' },
        },
        {
            id: 'onExpiry',
            type: 'select',
            label: 'When it reaches zero',
            group: 'Timer',
            info: 'Restart only applies to a duration per visitor.',
            default: 'hide',
            options: [
                { value: 'hide', label: 'Hide the section' },
                { value: 'zero', label: 'Stay at zero' },
                { value: 'message', label: 'Show a message' },
                { value: 'restart', label: 'Restart' },
            ],
        },
        {
            id: 'expiredMessage',
            // CONTENT, not an interface string: the seller rewrites it, so a duplicated shop must translate
            // what THEY wrote (0068 lot C). `aiWritable: true` is the documented override of the `defaultKey`
            // gate for exactly this case — a dictionary-backed default that is a placeholder, not a label.
            aiWritable: true,
            type: 'text',
            label: 'End message',
            group: 'Timer',
            default: 'Offre terminée',
            defaultKey: 'section.countdown_expired',
            maxLength: 80,
            visibleWhen: { field: 'onExpiry', equals: 'message' },
        },
        // ── Content ──────────────────────────────────────────────────────────────────
        {
            id: 'title',
            // CONTENT, not an interface string: the seller rewrites it, so a duplicated shop must translate
            // what THEY wrote (0068 lot C). `aiWritable: true` is the documented override of the `defaultKey`
            // gate for exactly this case — a dictionary-backed default that is a placeholder, not a label.
            aiWritable: true,
            type: 'text',
            label: 'Heading',
            group: 'Content',
            default: 'Vente flash',
            defaultKey: 'section.countdown_title',
            maxLength: 80,
        },
        {
            id: 'subtitle',
            // CONTENT, not an interface string: the seller rewrites it, so a duplicated shop must translate
            // what THEY wrote (0068 lot C). `aiWritable: true` is the documented override of the `defaultKey`
            // gate for exactly this case — a dictionary-backed default that is a placeholder, not a label.
            aiWritable: true,
            type: 'text',
            label: 'Subheading',
            group: 'Content',
            default: 'L’offre se termine dans :',
            defaultKey: 'section.countdown_subtitle',
            maxLength: 140,
        },
        {
            id: 'daysDisplay',
            type: 'select',
            label: 'Days',
            group: 'Content',
            default: 'auto',
            options: [
                { value: 'auto', label: 'Only past 24 h' },
                { value: 'always', label: 'Always' },
                { value: 'never', label: 'Never' },
            ],
        },
        {
            id: 'showSeconds',
            type: 'checkbox',
            label: 'Show seconds',
            group: 'Content',
            default: true,
        },
        {
            id: 'showLabels',
            type: 'checkbox',
            label: 'Show unit names',
            group: 'Content',
            default: true,
        },
        // The four words under the digits. Empty = the store language's own word, so a shop that never
        // touches them is translated into all 26 languages for free; a shop that wants "Jours" instead of
        // "JOURS", or a dialect word, types it here. `aiWritable: false`: these are interface words, not
        // prose, and a generated sentence under a digit is exactly the failure that flag exists for.
        {
            id: 'labelDays',
            aiWritable: false,
            type: 'text',
            label: 'Word for days',
            group: 'Content',
            info: 'Empty = the word of your store language.',
            default: '',
            maxLength: 16,
            visibleWhen: { field: 'showLabels', equals: true },
        },
        {
            id: 'labelHours',
            aiWritable: false,
            type: 'text',
            label: 'Word for hours',
            group: 'Content',
            default: '',
            maxLength: 16,
            visibleWhen: { field: 'showLabels', equals: true },
        },
        {
            id: 'labelMinutes',
            aiWritable: false,
            type: 'text',
            label: 'Word for minutes',
            group: 'Content',
            default: '',
            maxLength: 16,
            visibleWhen: { field: 'showLabels', equals: true },
        },
        {
            id: 'labelSeconds',
            aiWritable: false,
            type: 'text',
            label: 'Word for seconds',
            group: 'Content',
            default: '',
            maxLength: 16,
            visibleWhen: { field: 'showLabels', equals: true },
        },
        {
            id: 'separator',
            aiWritable: false, // a glyph between two tiles, not a sentence
            type: 'select',
            label: 'Between the units',
            group: 'Content',
            default: 'none',
            options: [
                { value: 'none', label: 'Nothing' },
                { value: 'colon', label: 'Colon' },
                { value: 'dot', label: 'Dot' },
                { value: 'slash', label: 'Slash' },
            ],
        },
        // ── Button ───────────────────────────────────────────────────────────────────
        {
            id: 'ctaLabel',
            // CONTENT, not an interface string: the seller rewrites it, so a duplicated shop must translate
            // what THEY wrote (0068 lot C). `aiWritable: true` is the documented override of the `defaultKey`
            // gate for exactly this case — a dictionary-backed default that is a placeholder, not a label.
            aiWritable: true,
            type: 'text',
            label: 'Button label',
            group: 'Button',
            default: 'J’en profite',
            defaultKey: 'section.countdown_cta',
            maxLength: 40,
        },
        {
            id: 'cta',
            type: 'link',
            label: 'Button link',
            group: 'Button',
            info: 'No link, no button — a button that goes nowhere reads as a broken shop.',
        },
        {
            id: 'ctaStyle',
            type: 'select',
            label: 'Button style',
            group: 'Button',
            default: 'solid',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'outline', label: 'Outline' },
                { value: 'text', label: 'Text only' },
            ],
        },
        {
            id: 'ctaSize',
            type: 'select',
            label: 'Button size',
            group: 'Button',
            default: 'medium',
            options: [
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' },
            ],
        },
        {
            id: 'ctaFullWidthMobile',
            type: 'checkbox',
            label: 'Full width on mobile',
            group: 'Button',
            default: false,
        },
        // ── Desktop / Mobile ─────────────────────────────────────────────────────────
        // The inspector draws a group literally named `Desktop` or `Mobile` as a DEVICE PART, with its own
        // glyph and heading (`SectionForm.DEVICE_GROUPS`). That is the shape the founder asked for on
        // 24/09/2026 — "le panneau se lit en deux parties" — so a countdown, which is read on a phone first,
        // uses it rather than inventing a third arrangement.
        {
            id: 'layout',
            type: 'select',
            label: 'Layout',
            group: 'Desktop',
            default: 'stacked',
            options: [
                { value: 'stacked', label: 'Text above the clock' },
                { value: 'inline', label: 'Everything on one line' },
                { value: 'split', label: 'Text left, clock right' },
            ],
        },
        {
            id: 'align',
            type: 'select',
            label: 'Alignment',
            group: 'Desktop',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'titleSize',
            type: 'range',
            label: 'Heading size',
            group: 'Desktop',
            default: 24,
            min: 10,
            max: 80,
            step: 1,
            unit: 'px',
        },
        {
            id: 'titleWeight',
            type: 'select',
            label: 'Heading weight',
            group: 'Desktop',
            default: '700',
            options: [
                { value: '400', label: 'Regular' },
                { value: '500', label: 'Medium' },
                { value: '600', label: 'Semi bold' },
                { value: '700', label: 'Bold' },
                { value: '800', label: 'Extra bold' },
                { value: '900', label: 'Black' },
            ],
        },
        {
            id: 'subtitleSize',
            type: 'range',
            label: 'Subheading size',
            group: 'Desktop',
            default: 14,
            min: 8,
            max: 40,
            step: 1,
            unit: 'px',
        },
        {
            id: 'digitSize',
            type: 'range',
            label: 'Digit size',
            group: 'Desktop',
            default: 34,
            min: 10,
            max: 120,
            step: 1,
            unit: 'px',
        },
        {
            id: 'digitWeight',
            type: 'select',
            label: 'Digit weight',
            group: 'Desktop',
            default: '700',
            options: [
                { value: '400', label: 'Regular' },
                { value: '500', label: 'Medium' },
                { value: '600', label: 'Semi bold' },
                { value: '700', label: 'Bold' },
                { value: '800', label: 'Extra bold' },
                { value: '900', label: 'Black' },
            ],
        },
        {
            id: 'labelSize',
            type: 'range',
            label: 'Unit name size',
            group: 'Desktop',
            default: 10,
            min: 6,
            max: 28,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'showLabels', equals: true },
        },
        {
            id: 'tileShape',
            type: 'select',
            label: 'Tile shape',
            group: 'Desktop',
            default: 'rounded',
            options: [
                { value: 'none', label: 'No tile' },
                { value: 'rounded', label: 'Rounded' },
                { value: 'square', label: 'Square' },
                { value: 'circle', label: 'Circle' },
            ],
        },
        {
            id: 'tileSize',
            type: 'range',
            label: 'Tile size',
            group: 'Desktop',
            default: 76,
            min: 28,
            max: 200,
            step: 2,
            unit: 'px',
            visibleWhen: { field: 'tileShape', notEquals: 'none' },
        },
        {
            id: 'tileRadius',
            type: 'range',
            label: 'Tile corner radius',
            group: 'Desktop',
            default: 14,
            min: 0,
            max: 60,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'tileShape', equals: 'rounded' },
        },
        {
            id: 'tileGap',
            type: 'range',
            label: 'Space between units',
            group: 'Desktop',
            default: 12,
            min: 0,
            max: 60,
            step: 1,
            unit: 'px',
        },
        {
            id: 'borderWidth',
            type: 'range',
            label: 'Tile border',
            group: 'Desktop',
            default: 0,
            min: 0,
            max: 10,
            step: 1,
            unit: 'px',
            visibleWhen: { field: 'tileShape', notEquals: 'none' },
        },
        {
            id: 'shadow',
            type: 'select',
            label: 'Tile shadow',
            group: 'Desktop',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'soft', label: 'Soft' },
                { value: 'strong', label: 'Strong' },
            ],
            visibleWhen: { field: 'tileShape', notEquals: 'none' },
        },
        // ── Mobile ───────────────────────────────────────────────────────────────────
        {
            id: 'mobileLayout',
            type: 'select',
            label: 'Layout',
            group: 'Mobile',
            default: 'stacked',
            options: [
                { value: 'stacked', label: 'Text above the clock' },
                { value: 'inline', label: 'Everything on one line' },
                { value: 'split', label: 'Text left, clock right' },
            ],
        },
        {
            id: 'mobileTitleSize',
            type: 'range',
            label: 'Heading size',
            group: 'Mobile',
            default: 20,
            min: 10,
            max: 64,
            step: 1,
            unit: 'px',
        },
        {
            id: 'mobileSubtitleSize',
            type: 'range',
            label: 'Subheading size',
            group: 'Mobile',
            default: 13,
            min: 8,
            max: 32,
            step: 1,
            unit: 'px',
        },
        {
            id: 'mobileDigitSize',
            type: 'range',
            label: 'Digit size',
            group: 'Mobile',
            default: 26,
            min: 10,
            max: 96,
            step: 1,
            unit: 'px',
        },
        {
            id: 'mobileTileSize',
            type: 'range',
            label: 'Tile size',
            group: 'Mobile',
            default: 58,
            min: 24,
            max: 160,
            step: 2,
            unit: 'px',
            visibleWhen: { field: 'tileShape', notEquals: 'none' },
        },
        // ── Colors ───────────────────────────────────────────────────────────────────
        // All `auto`: untouched, the component derives them from the section's colour scheme, so a countdown
        // dropped on a page is already coherent with the theme. Touched, it is exactly what the picker showed.
        {
            id: 'digitColor',
            type: 'color',
            label: 'Digits',
            group: 'Countdown colors',
            auto: true,
        },
        {
            id: 'tileColor',
            type: 'color',
            label: 'Tile background',
            group: 'Countdown colors',
            auto: true,
            visibleWhen: { field: 'tileShape', notEquals: 'none' },
        },
        {
            id: 'labelColor',
            type: 'color',
            label: 'Unit names',
            group: 'Countdown colors',
            auto: true,
            visibleWhen: { field: 'showLabels', equals: true },
        },
        {
            id: 'borderColor',
            type: 'color',
            label: 'Tile border',
            group: 'Countdown colors',
            auto: true,
            visibleWhen: { field: 'borderWidth', notEquals: 0 },
        },
        {
            id: 'buttonColor',
            type: 'color',
            label: 'Button',
            group: 'Countdown colors',
            auto: true,
        },
        {
            id: 'buttonTextColor',
            type: 'color',
            label: 'Button label',
            group: 'Countdown colors',
            auto: true,
        },
        // ── Visibility ───────────────────────────────────────────────────────────────
        {
            id: 'visibility',
            type: 'select',
            label: 'Visibility',
            group: 'Visibility',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'desktop-hidden', label: 'Mobile only' },
                { value: 'mobile-hidden', label: 'Desktop only' },
            ],
        },
        // Heading/body colours + gradients, the same two roles every other section paints with.
        ...textColorFields(),
        // The universal group, hand-rolled (the custom-columns idiom): colour scheme + width stay in their
        // own part, but the paddings go INTO the device parts above — a seller setting up the phone should
        // not have to scroll past both device sections to find the phone's own padding.
        ...sectionChromeFields,
        ...paddingFields('Desktop'),
        ...mobilePaddingFields('Mobile'),
        ...animationFields,
    ],
};
/**
 * Four starting points in "Add section". A preset is the whole look in one click — the seller then turns
 * whatever they want, which is the point of every knob above.
 */
export const countdownPresets = [
    {
        name: 'Countdown',
        settings: {
            timerType: 'date',
            onExpiry: 'hide',
            daysDisplay: 'auto',
            showSeconds: true,
            showLabels: true,
            separator: 'none',
            layout: 'stacked',
            mobileLayout: 'stacked',
            align: 'center',
            tileShape: 'rounded',
            tileSize: 76,
            mobileTileSize: 58,
            tileRadius: 14,
            tileGap: 12,
            digitSize: 34,
            mobileDigitSize: 26,
            colorScheme: 'light',
        },
    },
    {
        name: 'Countdown — dark bar',
        settings: {
            timerType: 'date',
            onExpiry: 'hide',
            layout: 'inline',
            mobileLayout: 'stacked',
            align: 'center',
            tileShape: 'none',
            tileGap: 18,
            digitSize: 30,
            mobileDigitSize: 24,
            titleSize: 18,
            mobileTitleSize: 16,
            separator: 'colon',
            colorScheme: 'inverse',
            paddingTop: 20,
            paddingBottom: 20,
            mobilePaddingTop: 16,
            mobilePaddingBottom: 16,
        },
    },
    {
        name: 'Countdown — circles',
        settings: {
            timerType: 'date',
            onExpiry: 'hide',
            layout: 'stacked',
            align: 'center',
            tileShape: 'circle',
            tileSize: 88,
            mobileTileSize: 64,
            tileGap: 14,
            digitSize: 30,
            mobileDigitSize: 22,
            labelSize: 9,
            colorScheme: 'light',
        },
    },
    {
        name: 'Countdown — flash sale',
        settings: {
            timerType: 'evergreen',
            minutes: 15,
            onExpiry: 'restart',
            layout: 'split',
            mobileLayout: 'stacked',
            align: 'left',
            daysDisplay: 'never',
            tileShape: 'rounded',
            tileRadius: 8,
            tileSize: 66,
            mobileTileSize: 54,
            digitSize: 28,
            mobileDigitSize: 24,
            colorScheme: 'accent',
            ctaSize: 'large',
            ctaFullWidthMobile: true,
        },
    },
];
/** How the section presents itself in "Add section" — label, group, icon (see the ticker for why it lives here). */
export const countdownMeta = {
    label: 'Countdown',
    group: 'Promotion',
    icon: 'clock',
};
//# sourceMappingURL=schema.js.map